import { useCallback, useRef, useState } from 'react';
import {
  importManufacturerHierarchy,
  importSpatialHierarchy,
  parseBiImportFailure,
  type BiImportFailure,
  type BiImportStage,
  type ImportManufacturerHierarchyResponse,
  type ImportSpatialHierarchyResponse,
} from '@repo/front-end-services';
import type { SetBiOutcomeInput } from '@repo/frontend-types';
import { biCaveats, type BiSummary } from './LaunchSuccessSummary';
import { biFailureReason } from './biPushCopy';

/**
 * Launch's BI step — `twin-launch-history-bi-push` R1, R5.
 *
 * After the twin's model and configuration are live, Go Live updates BI's spatial hierarchy, then
 * its manufacturer hierarchy. Kept out of `OnboardingPage.tsx` because it is self-contained state:
 * what each stage returned, which one failed and why, and the inputs a Retry needs.
 *
 * **A Retry redoes only the part that failed.** If the spatial update failed, it runs spatial then
 * manufacturer; if only the manufacturer update failed, it runs only manufacturer. Nothing before
 * the BI step — activation, filing — is ever re-run from here.
 */

export type BiPushStatus = 'idle' | 'running' | 'failed' | 'done';

export interface BiPushState {
  status: BiPushStatus;
  spatial?: ImportSpatialHierarchyResponse;
  /** `'skipped'` on a first launch with nothing placed (see `skipsManufacturer`). */
  manufacturer?: ImportManufacturerHierarchyResponse | 'skipped';
  failure?: BiImportFailure;
  /**
   * How many times this BI step has failed. The banner is keyed on it, so a retry that fails again
   * — even with an identical failure — remounts the alert and is announced again.
   */
  failures?: number;
}

export interface BiPushInputs {
  twinBid: string;
  /** The TWIN's organisation, as Go Live resolved it — never the operator's selection. */
  orgBID: string;
  accessToken: string;
  /**
   * No equipment was placed. The manufacturer import maps equipment, and with none it refuses
   * ("No Asset entities found") — which is not a failure of this launch, so the stage is skipped.
   */
  skipManufacturer: boolean;
}

/** The two import calls, injectable so the stage logic can be tested without the network. */
export interface BiImporters {
  spatial: typeof importSpatialHierarchy;
  manufacturer: typeof importManufacturerHierarchy;
}

const realImporters: BiImporters = {
  spatial: importSpatialHierarchy,
  manufacturer: importManufacturerHierarchy,
};

interface Options {
  middlewareApi: string;
  /** A fresh token for a Retry — the one Go Live held may have expired by then. */
  getToken: () => Promise<string>;
  importers?: BiImporters;
}

const IDLE: BiPushState = { status: 'idle' };

/**
 * Run the stages from `from` onwards. Pure apart from the calls it is given, so the resume rule is
 * testable directly.
 */
/**
 * Whether Launch skips the manufacturer update. Only on a first launch with nothing placed, when BI
 * holds no equipment for the twin yet. An update that removed the last piece of equipment still runs
 * it: BI's import function then recalculates the twin's tracked capacity and refreshes BI's
 * manufacturer and equipment-type views, which would otherwise keep the previous launch's equipment.
 */
export const skipsManufacturer = (updating: boolean, assetsLive: number): boolean =>
  !updating && assetsLive === 0;

export const runBiStages = async (
  from: BiImportStage,
  inputs: BiPushInputs,
  prior: BiPushState,
  middlewareApi: string,
  importers: BiImporters,
): Promise<BiPushState> => {
  const request = { twin_bid: inputs.twinBid, org_bid: inputs.orgBID };
  // The FML-era integrity scan is an organisation-wide dGraph read whose result the wizard does
  // not show; the wizard runs its own sensor checks before going live.
  const spatialRequest = { ...request, skip_integrity_check: true };
  let spatial = prior.spatial;
  let stage: BiImportStage = from;
  const startedAt = Date.now();
  try {
    if (from === 'spatial') {
      spatial = await importers.spatial(middlewareApi, inputs.accessToken, spatialRequest);
    }
    if (inputs.skipManufacturer) {
      console.info('[go-live:bi] updated', {
        twinBid: inputs.twinBid,
        manufacturer: 'skipped — nothing placed',
        ms: Date.now() - startedAt,
      });
      return { status: 'done', spatial, manufacturer: 'skipped' };
    }
    stage = 'manufacturer';
    const manufacturer = await importers.manufacturer(middlewareApi, inputs.accessToken, request);
    console.info('[go-live:bi] updated', { twinBid: inputs.twinBid, ms: Date.now() - startedAt });
    return { status: 'done', spatial, manufacturer };
  } catch (error) {
    const failure = parseBiImportFailure(error, stage);
    // The parsed failure only — never the error object, which carries the Bearer header.
    console.warn('[go-live:bi] not updated', {
      twinBid: inputs.twinBid,
      ms: Date.now() - startedAt,
      ...failure,
    });
    return { status: 'failed', spatial, failure, failures: (prior.failures ?? 0) + 1 };
  }
};

export const useLaunchBiPush = ({
  middlewareApi,
  getToken,
  importers = realImporters,
}: Options) => {
  const [state, setState] = useState<BiPushState>(IDLE);
  const stateRef = useRef<BiPushState>(IDLE);
  const inputsRef = useRef<BiPushInputs | null>(null);
  // Bumped by `reset`, so a stage still in flight when the person leaves Launch writes nothing.
  const generationRef = useRef(0);

  const commit = (next: BiPushState) => {
    stateRef.current = next;
    setState(next);
  };

  const runFrom = useCallback(
    async (from: BiImportStage, inputs: BiPushInputs): Promise<BiPushState> => {
      const generation = generationRef.current;
      const prior = stateRef.current;
      commit({ ...prior, status: 'running' });
      const next = await runBiStages(from, inputs, prior, middlewareApi, importers);
      if (generation !== generationRef.current) return next;
      commit(next);
      return next;
    },
    [middlewareApi, importers],
  );

  /** The BI step of a Go Live: spatial, then manufacturer. */
  const run = useCallback(
    (inputs: BiPushInputs): Promise<BiPushState> => {
      inputsRef.current = inputs;
      stateRef.current = IDLE;
      return runFrom('spatial', inputs);
    },
    [runFrom],
  );

  /** Redo only the part that failed, with a fresh token. */
  const retry = useCallback(async (): Promise<BiPushState> => {
    const inputs = inputsRef.current;
    const failed = stateRef.current;
    const failure = failed.failure;
    // Only from `failed`, so a second press while a retry runs does nothing.
    if (!inputs || !failure || failed.status !== 'failed') return failed;
    const generation = generationRef.current;
    // Running BEFORE the token await, so that window is covered by the same guard.
    commit({ ...failed, status: 'running' });
    let accessToken: string;
    try {
      accessToken = await getToken();
    } catch (error) {
      if (generation !== generationRef.current) return stateRef.current;
      console.warn('[go-live:bi] retry could not refresh the session', {
        twinBid: inputs.twinBid,
        stage: failure.stage,
        message: error instanceof Error ? error.message : String(error),
      });
      const next: BiPushState = {
        ...failed,
        status: 'failed',
        failures: (failed.failures ?? 0) + 1,
        failure: {
          stage: failure.stage,
          reason: 'other',
          message: 'Your session could not be refreshed — reload the page and sign in.',
          outcomeUnknown: false,
        },
      };
      commit(next);
      return next;
    }
    if (generation !== generationRef.current) return stateRef.current;
    const refreshed = { ...inputs, accessToken };
    inputsRef.current = refreshed;
    // Resume from the stage that failed, carrying what already succeeded.
    stateRef.current = failed;
    return runFrom(failure.stage, refreshed);
  }, [getToken, runFrom]);

  /** Forget everything — leaving Launch, where the plan may change. */
  const reset = useCallback(() => {
    generationRef.current += 1;
    inputsRef.current = null;
    commit(IDLE);
  }, []);

  return { state, run, retry, reset };
};

/**
 * A completed BI step, as the success summary reports it. `placed` is the number of equipment items
 * the launch activated — what the manufacturer update could at most have linked.
 */
export const toBiSummary = (state: BiPushState, placed: number): BiSummary => {
  const manufacturer = state.manufacturer;
  const ran = manufacturer !== undefined && manufacturer !== 'skipped';
  return {
    kind: 'updated',
    entitiesImported: state.spatial?.entitiesImported ?? 0,
    linked: ran ? manufacturer.equipmentMappingsImported : null,
    placed,
    // `null` is a check that could not run; `undefined` is a middleware from before this change,
    // which never checked. Both are unchecked — never "checked, no issues".
    validationIssues: !ran ? 0 : (manufacturer.validationIssues?.length ?? null),
    // The items behind the count (`launch-bi-gap-preview`, R3.1): `null` exactly when the count is.
    issues: !ran
      ? []
      : (manufacturer.validationIssues?.map(i => ({
          entityBid: i.entity_bid,
          referenceId: i.reference_id,
          issue: i.issue ?? '',
        })) ?? null),
  };
};

/**
 * A completed BI step, as the launch history records it (spec twin-launch-history-bi-outcome, R1).
 * `updated_with_gaps` exactly when the summary's BI row is amber: both read `biCaveats`.
 */
export const toBiOutcome = (state: BiPushState, placed: number): SetBiOutcomeInput => {
  const summary = toBiSummary(state, placed);
  if (summary.kind !== 'updated') throw new Error('toBiSummary always answers updated');
  return {
    outcome: biCaveats(summary).length > 0 ? 'updated_with_gaps' : 'updated',
    detail: {
      entitiesImported: summary.entitiesImported,
      linked: summary.linked,
      placed: summary.placed,
      validationIssues: summary.validationIssues,
      manufacturerSkipped: state.manufacturer === 'skipped',
    },
  };
};

const failureDetail = (failure: BiImportFailure) => ({
  stage: failure.stage,
  reason: failure.reason,
  outcomeUnknown: failure.outcomeUnknown,
  httpStatus: failure.status ?? null,
});

/** A BI step that failed, recorded at once, before the person chooses Retry or Continue. */
export const toFailedBiOutcome = (failure: BiImportFailure): SetBiOutcomeInput => ({
  outcome: 'failed',
  reason: biFailureReason(failure),
  detail: failureDetail(failure),
});

/**
 * *Continue without BI update*: the same reason the summary's BI row shows. With no failure on hand
 * the row says "BI was not updated." and there is no detail to record.
 */
export const toNotUpdatedBiOutcome = (failure: BiImportFailure | undefined): SetBiOutcomeInput =>
  failure
    ? { outcome: 'not_updated', reason: biFailureReason(failure), detail: failureDetail(failure) }
    : { outcome: 'not_updated', reason: 'BI was not updated.' };
